// src/pages/admin/adminPageEditor/hooks/useBlockAutosave.js
import { useEffect, useRef, useCallback } from "react";
import { mergeBlockPatch } from "../utils/editorUtils";

export function useBlockAutosave({ pageId, updateBlock, setBlock }) {
  const saveTimerRef = useRef(null);
  const pendingPatchRef = useRef(null);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const scheduleAutosave = useCallback(() => {
    if (!pageId) return;

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const patch = pendingPatchRef.current;
      if (!patch) return;

      pendingPatchRef.current = null;

      try {
        const res = await updateBlock(pageId, patch);
        // res should be { nav } or { footer } depending on API
        const block = res?.nav || res?.footer;
        if (block) setBlock(block);
      } catch (e) {
        console.error("Block autosave failed:", e);
        pendingPatchRef.current = mergeBlockPatch(pendingPatchRef.current || {}, patch);
      }
    }, 450);
  }, [pageId, updateBlock, setBlock]);

  const patchBlockLive = useCallback(
    (patch) => {
      setBlock((prev) => mergeBlockPatch(prev, patch));
      pendingPatchRef.current = mergeBlockPatch(pendingPatchRef.current || {}, patch);
      scheduleAutosave();
    },
    [setBlock, scheduleAutosave]
  );

  const flushBlockAutosave = useCallback(async () => {
    if (!pageId) return;
    clearTimeout(saveTimerRef.current);

    const patch = pendingPatchRef.current;
    if (!patch) return;

    pendingPatchRef.current = null;

    try {
      const res = await updateBlock(pageId, patch);
      const block = res?.nav || res?.footer;
      if (block) setBlock(block);
    } catch (e) {
      console.error("Flush block autosave failed:", e);
    }
  }, [pageId, updateBlock, setBlock]);

  return { patchBlockLive, flushBlockAutosave };
}